import { Link } from "react-router-dom";

import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { RequestLog, Trace } from "@/components/ui/icons";
import { cn } from "@/lib/utils";

export enum TagLogEntityType {
  TraceEntity = "TRACE",
  RequestEntity = "REQUEST",
}

type TagLogEntityLinkSize = "xs" | "sm" | "md";

interface TagLogEntityLinkProps {
  entityType: TagLogEntityType;
  name: string;
  link?: string;
  className?: string;
  size?: TagLogEntityLinkSize;
}

const TAG_LOG_ENTITY_CONFIG: Record<
  TagLogEntityType,
  { Icon: typeof Trace; iconWrapperClassName: string }
> = {
  [TagLogEntityType.TraceEntity]: {
    Icon: Trace,
    iconWrapperClassName: "bg-violet-50 text-violet-600",
  },
  [TagLogEntityType.RequestEntity]: {
    Icon: RequestLog,
    iconWrapperClassName: "bg-sky-50 text-sky-600",
  },
};

const getSizeClasses = (size: TagLogEntityLinkSize) => {
  switch (size) {
    case "xs":
      return {
        container: "gap-1 px-1 py-0",
        iconWrapper: "p-1",
        icon: "h-3 w-3",
        text: "text-xs",
      };
    case "sm":
      return {
        container: "gap-1 px-1.5 py-0.5",
        iconWrapper: "p-1.5",
        icon: "h-3 w-3",
        text: "text-xs",
      };
    case "md":
    default:
      return {
        container: "gap-1 px-2 py-1",
        iconWrapper: "p-2",
        icon: "h-3 w-3",
        text: "text-xs",
      };
  }
};

export const isTagLogEntityType = (
  entityType: string | undefined,
): entityType is TagLogEntityType =>
  entityType === TagLogEntityType.TraceEntity || entityType === TagLogEntityType.RequestEntity;

export const TagLogEntityLink = ({
  entityType,
  name,
  link,
  className,
  size = "md",
}: TagLogEntityLinkProps) => {
  const sizeClasses = getSizeClasses(size);
  const { Icon, iconWrapperClassName } = TAG_LOG_ENTITY_CONFIG[entityType];

  const content = (
    <>
      <div
        className={cn("flex-shrink-0 rounded-full", sizeClasses.iconWrapper, iconWrapperClassName)}
      >
        <Icon className={sizeClasses.icon} />
      </div>
      <div className="flex min-w-0 flex-1 flex-row gap-2">
        <OverflowDisplayTooltip content={name}>
          <span
            className={cn("truncate whitespace-nowrap font-medium text-gray-900", sizeClasses.text)}
          >
            {name}
          </span>
        </OverflowDisplayTooltip>
      </div>
    </>
  );

  const baseClassName = cn(
    "inline-flex min-w-0 max-w-full items-center rounded-md border border-gray-300 bg-white",
    sizeClasses.container,
    link && "transition-colors hover:bg-gray-50",
    className,
  );

  if (link) {
    return (
      <Link to={link} className={baseClassName}>
        {content}
      </Link>
    );
  }

  return <div className={baseClassName}>{content}</div>;
};
